import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  Alert,
  Divider,
  Container,
  InputAdornment,
  styled
} from '@mui/material';
import {
  AttachMoney,
  CurrencyExchange,
  TrendingUp,
  ArrowForward
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import useCurrencyConversion from '../../hooks/useCurrencyConversion';

// Styled Components
const ModernButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
  borderRadius: 12,
  padding: '12px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  color: '#FFFFFF !important',
  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
    transform: 'translateY(-2px)',
    color: '#FFFFFF !important',
  },
  '& .MuiButton-endIcon': {
    color: '#FFFFFF !important',
  },
  '& .MuiButton-startIcon': {
    color: '#FFFFFF !important',
  },
  '&.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.6) !important',
  },
  transition: 'all 0.3s ease',
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#FAFAFA',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1976D2',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1976D2',
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    '&.Mui-focused': {
      color: '#1976D2',
    },
  },
}));

const ModernSelect = styled(Select)(({ theme }) => ({
  borderRadius: 12,
  backgroundColor: '#FAFAFA',
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#1976D2',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#1976D2',
    borderWidth: 2,
  },
}));

const PixStep1 = () => {
  const { isLoggedIn, authLoading, getToken } = useAuth();
  const navigate = useNavigate();
  const {
    exchangeRates,
    selectedCurrencyPair,
    setSelectedCurrencyPair,
    loadCompanyFeeSettings,
    getCurrentExchangeRate,
    getCurrencyPairInfo,
    getCurrencyInfo
  } = useCurrencyConversion();

  const [formData, setFormData] = useState({
    currencyPair: 'ARS_BRL',
    value: ''
  });

  const [loading, setLoading] = useState(false);

  // Verificar autenticação e carregar configurações
  useEffect(() => {
    if (authLoading) return;

    if (!isLoggedIn) {
      navigate('/signin');
      return;
    }

    // Carregar configurações de taxas da empresa
    const token = getToken();
    if (token) {
      loadCompanyFeeSettings(token);
    }
  }, [isLoggedIn, authLoading, navigate, getToken, loadCompanyFeeSettings]);

  // Verificar se o par selecionado ainda está ativo quando exchangeRates mudar
  useEffect(() => {
    if (formData.currencyPair && exchangeRates[formData.currencyPair]) {
      const selectedPairData = exchangeRates[formData.currencyPair];

      // Se o par selecionado foi desativado, selecionar o primeiro par ativo
      if (!selectedPairData.isActive) {
        const activePairs = Object.entries(exchangeRates).filter(([pair, rateData]) => rateData.isActive);

        if (activePairs.length > 0) {
          const [firstActivePair] = activePairs;
          console.log('🔄 Par desativado, mudando para:', firstActivePair[0]);

          setFormData(prev => ({
            ...prev,
            currencyPair: firstActivePair[0]
          }));

          setSelectedCurrencyPair(firstActivePair[0]);

          toast.info(`Par de moeda alterado para ${firstActivePair[0] === 'ARS_BRL' ? 'ARS → BRL' : 'BRL → ARS'}`);
        } else {
          // Se não há pares ativos, limpar seleção
          console.log('⚠️ Nenhum par de moeda ativo disponível');
          setFormData(prev => ({
            ...prev,
            currencyPair: ''
          }));

          toast.warning('Nenhum par de moeda ativo disponível');
        }
      }
    }
  }, [exchangeRates, formData.currencyPair, setSelectedCurrencyPair]);

  // Escutar evento customizado quando configurações são atualizadas
  useEffect(() => {
    const handleSettingsUpdated = () => {
      const token = getToken();
      if (token) {
        console.log('🔄 Recarregando configurações devido ao evento customizado');
        loadCompanyFeeSettings(token);
      }
    };

    window.addEventListener('feeSettingsUpdated', handleSettingsUpdated);
    return () => window.removeEventListener('feeSettingsUpdated', handleSettingsUpdated);
  }, [getToken, loadCompanyFeeSettings]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Se mudou o par de moeda, atualizar no hook também
    if (field === 'currencyPair') {
      setSelectedCurrencyPair(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.value || parseFloat(formData.value) <= 0) {
      toast.error('Por favor, insira um valor válido');
      return;
    }

    setLoading(true);

    try {
      // Simular delay de processamento
      await new Promise(resolve => setTimeout(resolve, 500));

      // Navegar para a segunda tela com os dados
      navigate('/pix/step2', {
        state: { formData }
      });

    } catch (error) {
      console.error('Erro ao processar:', error);
      toast.error('Erro ao processar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  // Obter informações do par de moeda selecionado
  const pairInfo = getCurrencyPairInfo();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: { xs: 2, md: 4 },
        pt: { xs: '80px', md: '100px' } // Compensar o header fixo
      }}
    >
      <Container maxWidth="sm">
        <Card
          elevation={8}
          sx={{
            borderRadius: 4,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: { xs: 4, md: 5 } }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#1976D2',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  fontSize: { xs: '1.5rem', md: '2rem' }
                }}
              >
                <CurrencyExchange sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }} />
                Valor de Venda
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Selecione a moeda e informe o valor
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Box component="form" onSubmit={handleSubmit}>
              {/* Seleção de Par de Moeda */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#666' }}>
                  Par de moeda
                </Typography>
                <FormControl fullWidth>
                  <ModernSelect
                    value={formData.currencyPair}
                    onChange={(e) => handleInputChange('currencyPair', e.target.value)}
                    startAdornment={
                      <InputAdornment position="start">
                        <TrendingUp color="action" />
                      </InputAdornment>
                    }
                  >
                    {Object.entries(exchangeRates)
                      .filter(([pair, rateData]) => rateData.isActive)
                      .map(([pair, rateData]) => (
                        <MenuItem key={pair} value={pair}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography>
                              {pair === 'ARS_BRL' ? '🇦🇷' : '🇧🇷'}
                            </Typography>
                            <Typography>
                              {pair === 'ARS_BRL' ? 'ARS → BRL' : 'BRL → ARS'}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                  </ModernSelect>
                </FormControl>

                {/* Mostrar cotação atual */}
                {formData.currencyPair && exchangeRates[formData.currencyPair] && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Cotação atual: {exchangeRates[formData.currencyPair].description}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      1 {formData.currencyPair.split('_')[0]} = {exchangeRates[formData.currencyPair].rate} {formData.currencyPair.split('_')[1]}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Valor */}
              <Box sx={{ mb: 5 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#666' }}>
                  Valor a ser convertido
                </Typography>
                <ModernTextField
                  fullWidth
                  type="number"
                  value={formData.value}
                  onChange={(e) => handleInputChange('value', e.target.value)}
                  placeholder="Digite o valor"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Botão de Continuar */}
              <ModernButton
                fullWidth
                type="submit"
                size="large"
                disabled={!formData.value || loading}
                endIcon={<ArrowForward />}
              >
                {loading ? 'Processando...' : 'Continuar'}
              </ModernButton>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default PixStep1;
